import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router";
import { useServices } from "@/app/use-services";
import { usePlan } from "@/features/plan/use-plan";
import { MEMORY_QUERY_KEY } from "@/features/you/keys";
import { Button } from "@/ui/Button";
import { Row } from "@/ui/Row";
import { Screen } from "@/ui/Screen";
import { Body, Display, Label, Tiny } from "@/ui/Text";
import { closingOf, durationLabel, questionOf } from "./call-summary";
import type { CallSummaryState } from "./VoiceCallPage";

function isSummaryState(value: unknown): value is CallSummaryState {
  return typeof value === "object" && value !== null && Array.isArray((value as CallSummaryState).transcripts);
}

/** What the call was about, what she left you with, and what she now remembers. */
export function CallSummaryPage() {
  const { api, analytics } = useServices();
  const navigate = useNavigate();
  const location = useLocation();
  const state: CallSummaryState = isSummaryState(location.state) ? location.state : { transcripts: [], seconds: 0, since: null };
  const since = state.since ? new Date(state.since) : null;

  const remembered = useQuery({
    queryKey: [...MEMORY_QUERY_KEY, "since", state.since],
    queryFn: () => api.fetchMemory(),
    select: (facts) => (since ? facts.filter((f) => f.created_at && f.created_at >= since) : []),
    enabled: since !== null,
  });

  // The call may have used the last free minutes: then the next step is the plan.
  const plan = usePlan();
  const locked = plan.data?.locked === true;

  const question = questionOf(state.transcripts);
  const closing = closingOf(state.transcripts);

  return (
    <Screen
      foot={
        locked ? (
          <Button
            label="Keep talking with Antar"
            onClick={() => {
              analytics.track({ name: "keep talking tapped" });
              navigate("/subscribe", { replace: true });
            }}
          />
        ) : (
          <Button label="Done" outlined onClick={() => navigate("/talk", { replace: true })} />
        )
      }
    >
      <Label className="mt-2">{durationLabel(state.seconds)}</Label>
      <Display size="md" className="mt-2.5">{question ?? (closing ? "" : "Nothing was said this time.")}</Display>
      {closing && <Body className="mt-2.5">{closing}</Body>}
      {remembered.data && remembered.data.length > 0 && (
        <>
          <Label className="mt-6">Antar remembered</Label>
          {remembered.data.map((fact) => <Row key={fact.id} value={fact.fact} />)}
          <Tiny className="mt-3">She’ll bring these up next time without being asked. Remove any of them in You → Memory.</Tiny>
        </>
      )}
    </Screen>
  );
}
